import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import { type ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { useBrowseAgentsUIStore } from '@atlassian/conversation-assistant-agent/controllers/browse-agents-ui';
import {
	BrowseAgentsFullscreen,
	BrowseAgentsSidebar,
} from '@atlassian/conversation-assistant-agent/ui/BrowseAgentsSidebarModal';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';

import messages from './messages';

type BrowseAgentsProps = {
	chatRefreshHeader?: React.ReactNode;
};

export const BrowseAgentsView = ({ chatRefreshHeader }: BrowseAgentsProps): React.JSX.Element => {
	di(BrowseAgentsFullscreen);
	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();
	const { fullScreen, elements } = useUIConfig();
	const { siteId, closeChat } = useGlobalConfig();
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'browse-agents-panel',
	});
	const [_, { resetBrowseAgentsUI }] = useBrowseAgentsUIStore();
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { createConversationOptimistically, sendMessage } = useChatStoreActions();

	useEffect(() => {
		return () => {
			resetBrowseAgentsUI();
		};
	}, [resetBrowseAgentsUI]);

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'rovoChatBrowseAgents',
		});
		// Should only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClose = useCallback(
		({ resetActiveMenu = true }: { resetActiveMenu?: boolean } = {}) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'agentsViewCloseButton',
			});

			if (resetActiveMenu) {
				setActiveMenu('none');
			}
			resetBrowseAgentsUI();
			closeChat();
		},
		[setActiveMenu, resetBrowseAgentsUI, sendAnalyticsEvent, closeChat],
	);

	const handleAgentChat = useCallback(
		(agent: Agent) => {
			createConversationOptimistically({
				sourceAgentId: agent.id,
			});
			setActiveMenu('none');
		},
		[setActiveMenu, createConversationOptimistically],
	);

	const handleConversationStarter = useCallback(
		(agent: Agent, conversationStarter: ConversationStarter) => {
			setActiveMenu('none');

			resetBrowseAgentsUI();

			createConversationOptimistically({
				sourceAgentId: agent.id,
			});
			sendMessage({
				agent,
				prompt: encodeAdf(conversationStarter.message),
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages,
				messageSources: {
					interactionSource: 'viewAgentConversationStarters',
					followUpSource: 'ai-mate',
				},
			});
		},
		[
			createConversationOptimistically,
			sendMessage,
			storeUserMessages,
			resetBrowseAgentsUI,
			setActiveMenu,
		],
	);

	if (fullScreen) {
		return (
			<Stack>
				{!!elements?.forceFullscreenHeader && chatRefreshHeader}
				<BrowseAgentsFullscreen
					siteId={siteId}
					onClickChat={handleAgentChat}
					handleClickConversationStarter={handleConversationStarter}
					assistanceServiceParams={assistanceServiceParams}
				/>
			</Stack>
		);
	}
	return (
		<BrowseAgentsSidebar
			chatRefreshHeader={chatRefreshHeader}
			onClose={() => handleClose()}
			siteId={siteId}
			onClickChat={handleAgentChat}
			handleClickConversationStarter={handleConversationStarter}
			assistanceServiceParams={assistanceServiceParams}
			singleColumn={!fullScreen}
			titleElement={
				fullScreen ? (
					<Heading size="medium" as="h2">
						{formatMessage(messages.headerTitle)}
					</Heading>
				) : (
					<Heading size="small" as="h2">
						{formatMessage(messages.headerTitle)}
					</Heading>
				)
			}
		/>
	);
};
